import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getNit, updateNit, type UpdateNitRequest } from "@/api/nits";
import { TextField } from "@/components/ui/TextField";
import { showToast } from "@/components/ui/Toast";
import { FormPageShell } from "@/components/ui/FormPageShell";
import { FormPageSkeleton } from "@/components/ui/FormPageSkeleton";
import { InvalidIdNotice } from "@/components/ui/InvalidIdNotice";

export const NitEditarPage = () => {
  const { nit: nitParam } = useParams<{ nit: string }>();
  const nitOriginal = decodeURIComponent(nitParam ?? "");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: nit, isLoading } = useQuery({
    queryKey: ["nit", nitOriginal],
    queryFn: () => getNit(nitOriginal),
    enabled: Boolean(nitOriginal),
  });

  const update = useMutation({
    mutationFn: (body: UpdateNitRequest) => updateNit(nitOriginal, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["nits"] });
      showToast("success", "NIT actualizado");
      navigate("/nits");
    },
    onError: (err: Error) =>
      showToast("error", "No se pudo actualizar", err.message),
  });

  useEffect(() => {
    if (nit) {
      document.title = `Editar ${nit.razon_social} — NITs`;
    }
  }, [nit]);

  if (!nitOriginal) {
    return <InvalidIdNotice label="NIT" />;
  }
  if (isLoading || !nit) {
    return <FormPageSkeleton />;
  }

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const data: UpdateNitRequest = {
      razon_social: String(form.get("razon_social") ?? "").trim(),
      dv: String(form.get("dv") ?? "").trim() || undefined,
      telefono: String(form.get("telefono") ?? "").trim() || undefined,
      correo_electronico:
        String(form.get("correo_electronico") ?? "").trim() || undefined,
      direccion: String(form.get("direccion") ?? "").trim() || undefined,
      ciudad: String(form.get("ciudad") ?? "").trim() || undefined,
      pais: String(form.get("pais") ?? "").trim() || undefined,
    };
    update.mutate(data);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <FormPageShell
        title="Editar NIT"
        submitLabel="Guardar"
        onCancel={() => navigate("/nits")}
        loading={update.isPending}
      >
        <form
          onSubmit={onSubmit}
          className="space-y-3 rounded-lg bg-white p-6 shadow-card"
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <TextField
              label="NIT"
              required
              className="sm:col-span-2"
              value={nit.nit}
              disabled
            />
            <TextField
              label="DV"
              name="dv"
              maxLength={5}
              defaultValue={nit.dv ?? ""}
            />
          </div>
          <TextField
            label="Razón Social"
            name="razon_social"
            required
            defaultValue={nit.razon_social}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Correo Electrónico"
              name="correo_electronico"
              type="email"
              defaultValue={nit.correo_electronico ?? ""}
            />
            <TextField
              label="Teléfono"
              name="telefono"
              defaultValue={nit.telefono ?? ""}
            />
          </div>
          <TextField
            label="Dirección"
            name="direccion"
            defaultValue={nit.direccion ?? ""}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Ciudad"
              name="ciudad"
              defaultValue={nit.ciudad ?? ""}
            />
            <TextField
              label="País"
              name="pais"
              defaultValue={nit.pais ?? ""}
            />
          </div>
        </form>
      </FormPageShell>
    </div>
  );
};
